import { Globe, Map as MapIcon } from 'lucide-react'
import { useBasemap } from '@/lib/basemap'

interface BasemapToggleProps {
  className?: string
}

/**
 * Floating map control that toggles between street and satellite basemaps.
 * Designed to sit on top of the map canvas (not in the site nav), matching
 * the affordance pattern of MapLibre / Mapbox style switchers.
 *
 * Icon shows the *target* state — globe when currently on street view (click
 * to go to satellite), map when on satellite (click to go to street).
 */
export function BasemapToggle({ className }: BasemapToggleProps) {
  const { kind, toggle } = useBasemap()
  const isSatellite = kind === 'satellite'

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isSatellite ? 'Switch to street view' : 'Switch to satellite view'}
      title={isSatellite ? 'Street view' : 'Satellite view'}
      className={[
        // h-11 keeps the toggle the same height as every other floating
        // chrome element on the map (CoordInput, MyLocationButton, CursorModeToolbar).
        'inline-flex items-center justify-center gap-2 h-11 cursor-pointer',
        // Mobile (text hidden): square 44×44 to match MyLocationButton
        // exactly. Desktop (sm+, text shown): expand to fit the label
        // via px-3 + w-auto. --radius-lg matches MyLocationButton's
        // corner rounding so the cluster reads as a unified pair.
        'w-11 sm:w-auto sm:px-3 rounded-[var(--radius-lg)]',
        'border border-[var(--color-border)]',
        'bg-[var(--color-elev-strong)] backdrop-blur-[14px]',
        'text-[var(--color-text)]',
        'font-[var(--font-mono)] text-[11px] uppercase tracking-[0.1em]',
        'shadow-[var(--shadow-coord)]',
        'transition-colors duration-200',
        'hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]/50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]',
        className ?? '',
      ].join(' ')}
    >
      {isSatellite ? <MapIcon size={14} aria-hidden /> : <Globe size={14} aria-hidden />}
      {/* Text label hidden below sm so the floating toggle stays a
          compact icon button on phones — keeps horizontal room for
          the Search card next to it. The aria-label + title still
          announce the action for screen readers + tooltip hover. */}
      <span className="hidden sm:inline">{isSatellite ? 'Street' : 'Satellite'}</span>
    </button>
  )
}
